import {
  MsalProvider,
  MsalAuthenticationTemplate,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  MsalAuthenticationResult,
} from '@azure/msal-react';
import {
  InteractionType,
  PublicClientApplication,
  AccountInfo,
} from '@azure/msal-browser';
import { authConfig, AuthConfig } from './authConfig';
import { createContext, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react';
import { useToken } from './hooks';

export type AppUser = {
  name?: string;
  username?: string;
  tenantId?: string;
  roles?: string[];
};
type UpdateAppUserFunction = (user: SetStateAction<AppUser>) => void;
type TUserContext = {
  user: AppUser;
  updateUser: UpdateAppUserFunction;
};

const defaultUserContext: TUserContext = {
  user: {},
  updateUser: () => {},
};

const fetchOrig = window.fetch;

/**
 * Get the authenticated account.
 * Used for getting authorization tokens for API requests.
 * @group Authentication
 * @category Utils
 */
export const GetActiveAccount = (): AccountInfo => {
  let activeAccount = authConfig.publicClientApplication.getActiveAccount();

  if (!activeAccount) {
    const accounts = authConfig.publicClientApplication.getAllAccounts();
    authConfig.publicClientApplication.setActiveAccount(accounts[0]);
    activeAccount = accounts[0];
  }

  return activeAccount;
};

/**
 * Context for storing the current user
 */
const UserContext = createContext<TUserContext | null>(null);

/**
 * Provider for the UserContext
 * @group Authentication
 * @category Components
 */
export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AppUser>({});

  const updateUser = (user: SetStateAction<AppUser>) => setUser(user);

  return <UserContext.Provider value={{ user, updateUser }}>{children}</UserContext.Provider>;
};

/**
 * Hook for accessing the current user
 * @group Authentication
 * @category Utils
 */
export const useUser = (): TUserContext => {
  const userContext = useContext(UserContext);

  return userContext || defaultUserContext;
};

const AuthErrorComponent: React.FunctionComponent<MsalAuthenticationResult> = ({ error }) => {
  return <div>An Error Occurred: {error ? error.errorCode : 'unknown error'}</div>;
};

const RedirectOnError = ({ children }: { children: ReactNode }) => {
  // TODO: including useGetCurrentUserQuery here breaks Storybook for some reason
  // commenting this out for now.

  // const userQuery = useGetCurrentUserQuery();

  // if (userQuery.isError && (userQuery.error as any).data) {
  //   // Thread through error message to landing app
  //   window.location.href =
  //     config.REACT_APP_ENC_LANDING_APP_URL +
  //     'error/' +
  //     (userQuery.error as any).data?.FriendlyMessage;
  //   return <></>;
  // }
  return <>{children}</>;
};

const RedirectOnLogout = ({ children }: { children: ReactNode }) => {
  const { pathname } = window.location;
  if (pathname == '/logout') {
    // Redirect to homepage
    window.location.href = '/'; //authConfig.postLogoutRedirectUri;
    return <></>;
  }

  return <>{children}</>;
};

/**
 * Parameters for the AuthProvider
 * @group Authentication
 * @category Components
 */
const InitializeUser = ({ children }: { children: ReactNode }) => {
  const { user, updateUser } = useUser();
  const account = GetActiveAccount();
  const { accessToken } = useToken();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!accessToken) return;

    if (account.name == user?.name && account.username == user?.username && account.tenantId == user?.tenantId) {
      setIsInitialized(true);
      return;
    }

    updateUser({
      name: account.name,
      username: account.username,
      tenantId: account.tenantId,
      roles: account.idTokenClaims?.roles || [],
    });
    setIsInitialized(true);
  }, [account, user, updateUser, accessToken]);

  // Intercept fetch to add the auth bearer token to the requests
  useEffect(() => {
    if (!accessToken) return;

    window.fetch = async (url, config) => {
      const urlString = url instanceof Request ? url.url : url;
      if (typeof urlString === 'string' && urlString.includes('blob.core.windows.net')) {
        return fetchOrig(url, config);
      }

      const _config: RequestInit = config || {};
      _config.headers = new Headers(_config.headers || {});
      _config.headers.delete('Authorization');
      _config.headers.append('Authorization', `Bearer ${accessToken}`);
      return fetchOrig(url, _config);
    };
  }, [accessToken]);

  return isInitialized && accessToken ? <>{children}</> : null;
};

/**
 * Parameters for the AuthProvider
 * @group Authentication
 * @category Types
 */
// export type AuthProviderProps = Omit<AuthConfig, 'publicClientApplication'>;
export type AuthProviderProps = AuthConfig;

/**
 * EncompaaS authentication provider. Wrap your app content with this component and pass it a configuration object to gain access to the Enweb API.
 * @param params the AuthProvider parameters
 * @group Authentication
 * @category Components
 */
export const AuthProvider = ({ children, ...config }: AuthProviderProps & { children: ReactNode }) => {
  if (!Object.isFrozen(authConfig)) {
    authConfig.clientId = config.clientId;
    authConfig.authority = config.authority;
    authConfig.scopes = config.scopes;
    authConfig.redirectUri = config.redirectUri;
    authConfig.postLogoutRedirectUri = config.postLogoutRedirectUri;
    authConfig.apiBaseUrl = config.apiBaseUrl;
    authConfig.publicClientApplication = new PublicClientApplication({
      auth: {
        clientId: config.clientId,
        authority: config.authority,
        redirectUri: config.redirectUri,
        postLogoutRedirectUri: config.postLogoutRedirectUri,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
    });

    Object.freeze(authConfig);
  }

  const loginRequest = {
    scopes: config.scopes,
  };

  return (
    <RedirectOnLogout>
      <MsalProvider instance={authConfig.publicClientApplication}>
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={loginRequest}
          errorComponent={AuthErrorComponent}
        >
          <RedirectOnError>
            <UserProvider>
              <InitializeUser>{children}</InitializeUser>
            </UserProvider>
          </RedirectOnError>
        </MsalAuthenticationTemplate>
      </MsalProvider>
    </RedirectOnLogout>
  );
};

/**
 * This component (and its children) will be rendered when authenticated
 * @param params the AuthenticatedChildren parameters
 * @group Authentication
 * @category Components
 */
export const AuthenticatedChildren = ({ children }: { children: ReactNode }) => {
  return <AuthenticatedTemplate>{children}</AuthenticatedTemplate>;
};

/**
 * This component (and its children) will be rendered when not authenticated
 * @param params the UnauthenticatedChildren parameters
 * @group Authentication
 * @category Components
 */
export const UnauthenticatedChildren = ({ children }: { children: ReactNode }) => {
  return <UnauthenticatedTemplate>{children}</UnauthenticatedTemplate>;
};
